<template>
  <div class="geo-fence-map-container">
    <!-- Search Bar (Simple Style) -->
    <div class="geo-fence-search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="Search by Address, Zip, Metro Area or Lat/Long"
        class="search-input"
        size="medium"
        clearable
         @keyup.enter.native="handleSearch"
         @input="onSearchInput"
         @clear="clearSearch">
        <template slot="prefix">
          <i class="el-icon-search"></i>
        </template>
         <template slot="append">
           <el-button 
             icon="el-icon-search" 
             @click="handleSearch"
             :loading="searching"
             :disabled="searching">
           </el-button>
         </template>
      </el-input>
    </div>
    
    <!-- Map Container -->
    <div 
      :id="mapId" 
      ref="mapContainer"
      class="geo-fence-map"
    ></div>
    
    
    <!-- Filter and Table (Simple Style) -->
    <div class="geo-fences-table-section" v-if="isCreateMode && draftFences.length > 0">
      <div class="filter-section">
        <el-input
          v-model="filterQuery"
          :placeholder="isConversionMode ? 'Filter by Geo-conversion Name' : 'Filter by Geo-Fence Name'"
          size="small"
          class="filter-input"
          clearable>
          <template slot="prefix">
            <i class="el-icon-search"></i>
          </template>
        </el-input>
      </div>
      
      <div class="table-wrapper">
        <el-table 
          :data="filteredDraftFences" 
          size="small" 
          class="fences-table"
          stripe>
          <el-table-column :label="isConversionMode ? 'GEO-CONVERSION' : 'GEO-FENCE'" min-width="200">
            <template slot-scope="scope">
              <div v-if="editingNameIndex === scope.$index" class="fence-name-edit">
                <el-input
                  v-model="editingNameValue"
                  size="small"
                  maxLength="255"
                  @blur="saveFenceName(scope.$index, null)"
                  @keyup.enter.native="saveFenceName(scope.$index, null)"
                  @keyup.esc.native="cancelEditName"
                  ref="nameInput"
                  class="fence-name-input-inline">
                </el-input>
              </div>
              <div v-else class="fence-name-display" @dblclick="startEditName(scope.$index, null, scope.row.name)">
                <span class="fence-name-text">{{ scope.row.name || `Fence ${scope.$index + 1}` }}</span>
                <i class="el-icon-edit fence-name-edit-icon" @click="startEditName(scope.$index, null, scope.row.name)"></i>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="geo_fence_type_name" label="Type" width="120"></el-table-column>
          <el-table-column label="Points" width="100">
            <template slot-scope="scope">
              {{ getDraftFencePointCount(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="120">
            <template slot-scope="scope">
              <el-button @click="removeDraftFence(scope.$index)" type="text" size="small" style="color: #f56c6c">Delete</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- Recency Dropdown -->
      <div v-if="!isConversionMode" class="recency-section">
        <label class="recency-label">Geo-Fence Recency:</label>
        <el-select
          v-model="recencyId"
          placeholder="Select Recency"
          size="small"
          class="recency-select"
          clearable
          :loading="loadingRecency"
          @change="onRecencyChange">
          <el-option
            v-for="option in recencyOptions"
            :key="option.id"
            :label="option.label"
            :value="option.id">
          </el-option>
        </el-select>
      </div>
    </div>
    
    <!-- Saved Fences List (Edit Mode) - Same layout as create mode -->
    <div class="geo-fences-table-section" v-if="!isCreateMode && fences.length > 0">
      <div class="filter-section">
        <el-input
          v-model="filterQuery"
          :placeholder="isConversionMode ? 'Filter by Geo-conversion Name' : 'Filter by Geo-Fence Name'"
          size="small"
          class="filter-input"
          clearable>
          <template slot="prefix">
            <i class="el-icon-search"></i>
          </template>
        </el-input>
      </div>
      
      <div class="table-wrapper">
        <el-table 
          :data="filteredFences" 
          size="small" 
          class="fences-table"
          stripe>
          <el-table-column :label="isConversionMode ? 'GEO-CONVERSION' : 'GEO-FENCE'" min-width="200">
            <template slot-scope="scope">
              <div v-if="editingNameId === scope.row.id" class="fence-name-edit">
                <el-input
                  v-model="editingNameValue"
                  size="small"
                  maxlength="255"
                  @blur="saveFenceName(null, scope.row.id)"
                  @keyup.enter.native="saveFenceName(null, scope.row.id)"
                  @keyup.esc.native="cancelEditName"
                  ref="nameInput"
                  class="fence-name-input-inline">
                </el-input>
              </div>
              <div v-else class="fence-name-display" @dblclick="startEditName(null, scope.row.id, scope.row.name)">
                <span class="fence-name-text">{{ scope.row.name || `Fence ${scope.$index + 1}` }}</span>
                <i class="el-icon-edit fence-name-edit-icon" @click="startEditName(null, scope.row.id, scope.row.name)"></i>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="geo_fence_type_name" label="Type" width="120"></el-table-column>
          <el-table-column label="Points" width="100">
            <template slot-scope="scope">
              {{ getPointCount(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="120">
            <template slot-scope="scope">
              <el-button @click="deleteFence(scope.row.id, scope.$index, scope.row.name)" type="text" size="small" style="color: #f56c6c">Delete</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- Recency Dropdown -->
      <div v-if="!isConversionMode" class="recency-section">
        <label class="recency-label">Geo-Fence Recency:</label>
        <el-select
          v-model="recencyId"
          placeholder="Select Recency"
          size="small"
          class="recency-select"
          clearable
          :loading="loadingRecency"
          @change="onRecencyChange">
          <el-option
            v-for="option in recencyOptions"
            :key="option.id"
            :label="option.label"
            :value="option.id">
          </el-option>
        </el-select>
      </div>
    </div>
  </div>
</template>

<script>
import { Input, Button, Table, TableColumn, Select, Option, Notification } from 'element-ui';

export default {
  name: 'GeoFenceMap',
  components: {
    [Input.name]: Input,
    [Button.name]: Button,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option
  },
  props: {
    campaignId: {
      type: [String, Number],
      required: false,
      default: ''
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    isDraftCampaign: {
      type: Boolean,
      default: true // Default to true for create mode (always draft)
    },
    mapId: {
      type: String,
      required: false,
      default: 'geo-fence-map'
    },
    isConversionMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      map: null,
      drawnItems: null,
      drawControl: null,
      fences: [],
      loading: false,
      saving: false,
      draftFences: [], // For create mode
      editingNameIndex: null, // Index of fence being edited (create mode)
      editingNameId: null, // ID of fence being edited (edit mode)
      editingNameValue: '', // Temporary value for editing name
      searchQuery: '',
      searching: false,
      searchMarker: null,
      filterQuery: '', // For filtering geo fence table
      recencyId: 4, // For recency selection (default: 1 month)
      recencyOptions: [], // Recency options from API
      loadingRecency: false,
      isClearing: false, // Flag to prevent event handlers during clearAll
      isDeletingFence: false, // Flag to prevent onPolygonDeleted from processing during deleteFence
      searchDebounceTimer: null, // For debouncing search input
      lastSearchTime: 0, // For rate limiting (1 request per second)
      searchCache: {}, // Cache for search results to avoid duplicate API calls
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || '' // Mapbox Geocoding API token
    };
  },
  mounted() {
    this.loadRecencyOptions();
    this.initLeafletWithErrorHandling();
  },
  beforeDestroy() {
    // Clear debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    
    // Remove map instance
    if (this.map) {
      try {
        this.map.remove();
      } catch (e) {
        console.warn('Error removing map in beforeDestroy:', e);
      }
      this.map = null;
    }
    
    // Clear map container reference
    const mapContainer = document.getElementById(this.mapId);
    if (mapContainer && mapContainer._leaflet_id) {
      mapContainer.innerHTML = '';
      delete mapContainer._leaflet_id;
    }
    
    // Clear other references
    this.drawnItems = null;
    this.drawControl = null;
  },
  methods: {
    initLeafletWithErrorHandling() {
      // Check if Leaflet is already loaded
      if (typeof window.L !== 'undefined') {
        this.initMap();
        return;
      }
      
      // Listen for Leaflet load events
      const leafletLoadHandler = () => {
        if (typeof window.L !== 'undefined') {
          this.initMap();
          window.removeEventListener('leaflet-loaded', leafletLoadHandler);
          window.removeEventListener('leaflet-error', leafletErrorHandler);
        }
      };
      
      const leafletErrorHandler = () => {
        // Leaflet CDN not available - emit event to parent to use default location
        console.warn('Leaflet CDN not available - falling back to default location');
        this.$emit('leaflet-unavailable');
        window.removeEventListener('leaflet-loaded', leafletLoadHandler);
        window.removeEventListener('leaflet-error', leafletErrorHandler);
      };
      
      // Listen for events
      window.addEventListener('leaflet-loaded', leafletLoadHandler);
      window.addEventListener('leaflet-error', leafletErrorHandler);
      
      // Fallback: Check after delay if events didn't fire
      setTimeout(() => {
        if (typeof window.L !== 'undefined') {
          this.initMap();
          window.removeEventListener('leaflet-loaded', leafletLoadHandler);
          window.removeEventListener('leaflet-error', leafletErrorHandler);
        } else {
          // Still not loaded after 3 seconds - assume CDN unavailable
          console.warn('Leaflet CDN not available after timeout - falling back to default location');
          this.$emit('leaflet-unavailable');
          window.removeEventListener('leaflet-loaded', leafletLoadHandler);
          window.removeEventListener('leaflet-error', leafletErrorHandler);
        }
      }, 3000);
    },
    initMap() {
      try {
        // Check if Leaflet is available
        if (typeof window.L === 'undefined') {
          console.warn('Leaflet is not available - falling back to default location');
          this.$emit('leaflet-unavailable');
          return;
        }
        
        const L = window.L; // Get Leaflet from global
        
        // Check if map container exists
        const mapContainer = document.getElementById(this.mapId);
        if (!mapContainer) {
          console.warn('Geo fence map container not found - falling back to default location');
          this.$emit('leaflet-unavailable');
          return;
        }
        
        // Check if this.map already exists and remove it first
        if (this.map) {
          try {
            this.map.remove();
          } catch (e) {
            console.warn('Failed to remove existing map:', e);
          }
          this.map = null;
          this.drawnItems = null;
          this.drawControl = null;
        }
        
        // Check if map container already has a map instance (prevent re-initialization)
        // Leaflet stores map instance reference in container._leaflet_id
        if (mapContainer._leaflet_id) {
          // Container already has a map instance, clear it
          // Clear innerHTML and remove leaflet_id to allow re-initialization
          mapContainer.innerHTML = '';
          delete mapContainer._leaflet_id;
        }
        
        // Initialize map - setView([latitude, longitude], zoom)
        this.map = L.map(this.mapId).setView([33.1, -97.5], 10);
        
        // Add tile layer (OpenStreetMap)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(this.map);
        
        // Initialize feature group to store drawn items
        this.drawnItems = new L.FeatureGroup();
        this.map.addLayer(this.drawnItems);
        
        // Initialize draw control
        this.drawControl = new L.Control.Draw({
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true,
            shapeOptions: {
              color: '#3388ff',
              fillColor: '#3388ff',
              fillOpacity: 0.2
            },
            maxPoints: 12 // Sesuai limit Simplifi API
          },
          rectangle: {
            shapeOptions: {
              color: '#3388ff',
              fillColor: '#3388ff',
              fillOpacity: 0.2
            }
          },
          circle: false,
          marker: false,
          circlemarker: false,
          polyline: false
        },
        edit: {
          featureGroup: this.drawnItems,
          remove: true
        }
      });
      
      // Add draw control to map
      this.map.addControl(this.drawControl);
      
      // Listen for drawing events
      this.map.on(L.Draw.Event.CREATED, this.onShapeCreated);
      this.map.on(L.Draw.Event.EDITSTART, this.onEditStart);
      this.map.on(L.Draw.Event.EDITED, this.onPolygonEdited);
      this.map.on(L.Draw.Event.DELETED, this.onPolygonDeleted);
      
      this.updateDrawingControl();
      } catch (error) {
        // If map initialization fails, gracefully fallback to default location
        console.error('Failed to initialize Leaflet map:', error);
        this.$emit('leaflet-unavailable');
        // Ensure map is null to prevent further errors
        this.map = null;
        this.drawnItems = null;
        this.drawControl = null;
      }
    },
    updateDrawingControl() {
      // Always enable drawing (auto-save is immediate, no form needed)
      if (!this.map || !this.drawControl) return;
      
      // Check if control is already on map
      const controls = this.map._controlContainer;
      const controlExists = controls && controls.querySelector('.leaflet-draw');
      
      if (!controlExists) {
        this.map.addControl(this.drawControl);
      }
    },
    
    onShapeCreated(e) {
      const L = window.L;
      let layer = e.layer;
      let coordinates = [];
      let shapeType = 'polygon'; // 'polygon' or 'square'
      
      // Handle rectangle: convert to polygon format
      if (layer instanceof L.Rectangle) {
        shapeType = 'square';
        const bounds = layer.getBounds();
        const sw = bounds.getSouthWest(); // Southwest corner
        const se = bounds.getSouthEast(); // Southeast corner
        const ne = bounds.getNorthEast(); // Northeast corner
        const nw = bounds.getNorthWest(); // Northwest corner
        
        // Convert rectangle to polygon coordinates (4 points: SW -> SE -> NE -> NW -> SW)
        // Format: [lng, lat] for Simplifi API
        coordinates = [
          [sw.lng, sw.lat], // Southwest
          [se.lng, se.lat], // Southeast
          [ne.lng, ne.lat], // Northeast
          [nw.lng, nw.lat], // Northwest
          [sw.lng, sw.lat]  // Close polygon (back to Southwest)
        ];
        
        // Remove rectangle and create polygon instead
        this.map.removeLayer(layer);
        
        // Create polygon from rectangle bounds
        // Leaflet polygon uses [lat, lng] format
        const polygon = L.polygon([
          [sw.lat, sw.lng],
          [se.lat, se.lng],
          [ne.lat, ne.lng],
          [nw.lat, nw.lng],
          [sw.lat, sw.lng] // Close polygon
        ], {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2
        });
        
        // Use polygon layer instead
        layer = polygon;
      } else {
        // Handle polygon: extract coordinates normally
        coordinates = this.extractCoordinates(layer);
      }
      
      // Validasi: maksimal 12 titik
      if (coordinates.length > 12) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Maximum 12 points allowed for geo fence
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
        this.map.removeLayer(layer);
        return;
      }
      
      // Validasi: minimal 4 titik
      if (coordinates.length < 4) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Minimum 4 points required
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
        this.map.removeLayer(layer);
        return;
      }
      
      // Validasi: tidak boleh self-intersecting (crossing polygon)
      if (this.hasSelfIntersection(coordinates)) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Self-intersecting polygons are not allowed. Please adjust your geo fence.
            </p>
          `,
          duration: 4000,
          dangerouslyUseHTMLString: true
        });
        this.map.removeLayer(layer);
        return;
      }
      
      // Auto-save for both modes (create and edit)
      const timestamp = Date.now();
      const defaultName = `${shapeType}_${timestamp}`;
      
      // Determine fence type based on conversion mode
      const fenceType = this.isConversionMode ? 'Conversion' : 'Target';
      
      const geoFence = {
        name: defaultName,
        geo_fence_type_name: fenceType,
        bid_area: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      };
      
      if (this.isCreateMode) {
        // Create Mode: Auto-save directly to draftFences
        const draftIndex = this.draftFences.length;
        this.draftFences.push(geoFence);
        
        // console.log('onShapeCreated (create mode) - shape added', {
        //   shapeName: defaultName,
        //   shapeType: shapeType,
        //   draftIndex: draftIndex,
        //   totalDraftFences: this.draftFences.length,
        //   drawnItemsLayersCount: this.drawnItems ? this.drawnItems.getLayers().length : 0,
        //   isClearing: this.isClearing
        // });
        
        // Store layer with coordinates and draftIndex
        layer._geoFenceData = {
          coordinates: coordinates,
          name: defaultName,
          type: fenceType,
          draftIndex: draftIndex
        };
        
        // Add to drawnItems
        this.drawnItems.addLayer(layer);
        
        // Auto-save: immediately emit update to parent component
        // Parent will update geoFences and automatically toggle useGeoFence to true
        const fencesToEmit = JSON.parse(JSON.stringify(this.draftFences));
        // console.log('onShapeCreated (create mode) emitting fences-updated', {
        //   fencesCount: fencesToEmit.length,
        //   isClearing: this.isClearing
        // });
        if (!this.isClearing) {
          this.$emit('fences-updated', fencesToEmit);
        }
      } else {
        // Edit Mode: Auto-save directly to fences (without ID, will get ID when campaign is saved)
        this.fences.push(geoFence);
        
        // Store layer with coordinates (without ID because it's newly created)
        layer._geoFenceData = {
          coordinates: coordinates,
          name: defaultName,
          type: fenceType,
          id: null // New fence, no ID yet
        };
        
        // Add to drawnItems
        this.drawnItems.addLayer(layer);
        
        // Auto-save: immediately emit update to parent component
        const fencesToEmit = JSON.parse(JSON.stringify(this.fences));
        // console.log('onShapeCreated (edit mode) emitting fences-updated', {
        //   fencesCount: fencesToEmit.length,
        //   isClearing: this.isClearing
        // });
        if (!this.isClearing) {
          this.$emit('fences-updated', fencesToEmit);
        }
      }
    },
    
    onEditStart(e) {
      // Save original coordinates before edit starts
      const L = window.L;
      const layers = e.layers || this.drawnItems;
      
      layers.eachLayer((layer) => {
        // Save original LatLngs (Leaflet format: [[lat, lng], ...])
        const originalLatLngs = layer.getLatLngs()[0];
        // Deep copy to prevent reference issues
        layer._originalLatLngs = JSON.parse(JSON.stringify(originalLatLngs));
      });
    },
    
    onPolygonEdited(e) {
      const L = window.L;
      const layers = e.layers;
      layers.eachLayer((layer) => {
        const coordinates = this.extractCoordinates(layer);
        
        // Validasi ulang setelah edit
        if (coordinates.length > 12) {
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Maximum 12 points allowed
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
          // Restore original coordinates
          if (layer._originalLatLngs) {
            layer.setLatLngs([layer._originalLatLngs]);
          }
          return;
        }
        
        // Validasi: tidak boleh self-intersecting (crossing polygon) setelah edit
        if (this.hasSelfIntersection(coordinates)) {
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Self-intersecting polygons are not allowed. Please adjust your geo fence.
              </p>
            `,
            duration: 4000,
            dangerouslyUseHTMLString: true
          });
          // Restore original coordinates if invalid
          if (layer._originalLatLngs) {
            layer.setLatLngs([layer._originalLatLngs]);
          }
          return;
        }
        
        if (!layer._geoFenceData) return;
        
        // Update coordinates di layer
        layer._geoFenceData.coordinates = coordinates;
        
        // Update directly to fences/draftFences array
        if (this.isCreateMode) {
          // Create mode: update draftFences
          if (layer._geoFenceData.draftIndex !== null) {
            const index = layer._geoFenceData.draftIndex;
            if (this.draftFences[index]) {
              this.draftFences[index].bid_area.coordinates[0] = coordinates;
              // Auto-save: immediately emit update
              this.$emit('fences-updated', JSON.parse(JSON.stringify(this.draftFences)));
            }
          }
        } else {
          // Edit mode: update fences
          const fenceId = layer._geoFenceData.id;
          if (fenceId) {
            // Update existing fence - only update coordinates, keep name and type
            const fenceIndex = this.fences.findIndex(f => f.id === fenceId);
            if (fenceIndex !== -1) {
              this.fences[fenceIndex].bid_area.coordinates[0] = coordinates;
              // Auto-save: immediately emit update
              this.$emit('fences-updated', JSON.parse(JSON.stringify(this.fences)));
            }
          } else {
            // New fence (no ID yet) - find by matching name
            const fenceName = layer._geoFenceData.name;
            const fenceIndex = this.fences.findIndex(f => {
              // Match by name for new fence (without ID)
              return !f.id && f.name === fenceName;
            });
            if (fenceIndex !== -1) {
              this.fences[fenceIndex].bid_area.coordinates[0] = coordinates;
              // Auto-save: immediately emit update
              this.$emit('fences-updated', JSON.parse(JSON.stringify(this.fences)));
            }
          }
        }
      });
    },
    
    /**
     * Check if a polygon has self-intersections (crossing edges).
     * coordinates format: [[lng, lat], [lng, lat], ...] with last point equal to first.
     */
    hasSelfIntersection(coordinates) {
      if (!coordinates || coordinates.length < 4) {
        return false;
      }
      
      // Ensure last point is same as first for easier loop logic
      const pts = coordinates.slice();
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        pts.push([first[0], first[1]]);
      }
      
      const n = pts.length;
      
      // Helper: check if two segments (p1-q1) and (p2-q2) intersect (excluding shared endpoints)
      const segmentsIntersect = (p1, q1, p2, q2) => {
        // Based on orientation/CCW test
        const cross = (a, b, c) => {
          return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
        };
        
        const onSegment = (a, b, c) => {
          return (
            Math.min(a[0], c[0]) <= b[0] && b[0] <= Math.max(a[0], c[0]) &&
            Math.min(a[1], c[1]) <= b[1] && b[1] <= Math.max(a[1], c[1])
          );
        };
        
        const d1 = cross(p1, q1, p2);
        const d2 = cross(p1, q1, q2);
        const d3 = cross(p2, q2, p1);
        const d4 = cross(p2, q2, q1);
        
        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
            ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
          return true;
        }
        
        // Colinear special cases
        if (d1 === 0 && onSegment(p1, p2, q1)) return true;
        if (d2 === 0 && onSegment(p1, q2, q1)) return true;
        if (d3 === 0 && onSegment(p2, p1, q2)) return true;
        if (d4 === 0 && onSegment(p2, q1, q2)) return true;
        
        return false;
      };
      
      // Check every pair of non-adjacent segments
      for (let i = 0; i < n - 1; i++) {
        const p1 = pts[i];
        const q1 = pts[i + 1];
        
        for (let j = i + 1; j < n - 1; j++) {
          const p2 = pts[j];
          const q2 = pts[j + 1];
          
          // Skip adjacent segments (sharing a vertex)
          if (i === j || i + 1 === j || (i === 0 && j === n - 2)) {
            continue;
          }
          
          if (segmentsIntersect(p1, q1, p2, q2)) {
            return true;
          }
        }
      }
      
      return false;
    },
    
    onPolygonDeleted(e) {
      // Skip processing if clearAll or deleteFence is in progress
      if (this.isClearing || this.isDeletingFence) {
        // console.log('onPolygonDeleted skipped', {
        //   isClearing: this.isClearing,
        //   isDeletingFence: this.isDeletingFence
        // });
        return;
      }
      
      const layers = e.layers;
      layers.eachLayer((layer) => {
        if (!layer._geoFenceData) return;
        
        // Skip if arrays are already empty (e.g., during clearAll)
        // Also check flags again (in case they were set during iteration)
        if (this.isClearing || this.isDeletingFence) {
          // console.log('onPolygonDeleted skipped - flag detected during iteration', {
          //   isClearing: this.isClearing,
          //   isDeletingFence: this.isDeletingFence
          // });
          return;
        }
        
        if (this.isCreateMode && this.draftFences.length === 0) {
          return;
        }
        if (!this.isCreateMode && this.fences.length === 0) {
          return;
        }
        
        if (this.isCreateMode) {
          // Create mode: remove from draftFences by matching name (more reliable than draftIndex)
          // console.log('onPolygonDeleted (create mode) - processing deletion', {
          //   hasDraftIndex: layer._geoFenceData.draftIndex !== null,
          //   draftIndex: layer._geoFenceData.draftIndex,
          //   currentDraftFencesCount: this.draftFences.length,
          //   drawnItemsLayersCount: this.drawnItems ? this.drawnItems.getLayers().length : 0,
          //   layerName: layer._geoFenceData.name
          // });
          
          // Find fence in array by name (more reliable than using draftIndex which may be out of sync)
          const fenceName = layer._geoFenceData.name;
          const fenceIndex = this.draftFences.findIndex(f => f.name === fenceName);
          
          if (fenceIndex !== -1) {
            const deletedFence = this.draftFences[fenceIndex];
            this.draftFences.splice(fenceIndex, 1);
            
            // console.log('onPolygonDeleted (create mode) - fence removed from array', {
            //   deletedIndex: fenceIndex,
            //   deletedFenceName: deletedFence.name,
            //   remainingDraftFencesCount: this.draftFences.length
            // });
            
            // Update draftIndex for remaining layers to match their new array positions
            // Re-sync all draftIndex values to match current array positions
            this.draftFences.forEach((fence, newIndex) => {
              // Find layer with matching name and update its draftIndex
              this.drawnItems.eachLayer((remainingLayer) => {
                if (remainingLayer._geoFenceData && 
                    remainingLayer._geoFenceData.name === fence.name) {
                  remainingLayer._geoFenceData.draftIndex = newIndex;
                }
              });
            });
            
            // Auto-save: immediately emit update
            const fencesToEmit = JSON.parse(JSON.stringify(this.draftFences));
            // console.log('onPolygonDeleted (create mode) emitting fences-updated', {
            //   fencesCount: fencesToEmit.length,
            //   isClearing: this.isClearing
            // });
            if (!this.isClearing) {
              this.$emit('fences-updated', fencesToEmit);
            }
          } else {
            console.warn('onPolygonDeleted (create mode) - fence not found in draftFences', {
              fenceName: fenceName,
              draftFencesCount: this.draftFences.length,
              draftFencesNames: this.draftFences.map(f => f.name)
            });
          }
        } else {
          // Edit mode: directly remove from fences array
          
          // Prevent deleting the last fence for non-draft campaigns
          // Non-draft campaigns must have at least one geo fence (geo-targeting mode is locked)
          if (!this.isDraftCampaign && this.fences.length <= 1) {
            // Restore the layer to the map
            if (this.drawnItems && !this.drawnItems.hasLayer(layer)) {
              this.drawnItems.addLayer(layer);
            }
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Cannot Delete</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  This campaign has already run and uses Geo Fences. At least one geo fence must remain. 
                  The Geo Targeting mode cannot be changed for campaigns that have already run.
                </p>
              `,
              duration: 5000,
              dangerouslyUseHTMLString: true
            });
            return;
          }
          
          const fenceId = layer._geoFenceData.id;
          if (fenceId) {
            // Existing fence - remove from array by ID
            // console.log('onPolygonDeleted (edit mode) - removing fence by ID', {
            //   fenceId: fenceId,
            //   fencesCountBefore: this.fences.length
            // });
            this.fences = this.fences.filter(f => f.id !== fenceId);
          } else {
            // New fence (no ID yet) - remove by matching name (exact match only)
            const fenceName = layer._geoFenceData.name;
            // console.log('onPolygonDeleted (edit mode) - removing new fence by name', {
            //   fenceName: fenceName,
            //   fencesCountBefore: this.fences.length,
            //   unsavedFences: this.fences.filter(f => !f.id).map(f => ({ name: f.name, nameMatch: f.name === fenceName }))
            // });
            
            // Only remove fence with exact name match and no ID
            const beforeFilter = this.fences.length;
            this.fences = this.fences.filter(f => {
              // Always keep fences with ID (existing saved fences)
              if (f.id) {
                return true;
              }
              // For fences without ID, only remove if name exactly matches
              // This ensures we only remove the specific fence that was deleted, not all unsaved fences
              return f.name !== fenceName;
            });
            
            const afterFilter = this.fences.length;
            // console.log('onPolygonDeleted (edit mode) - filter result', {
            //   beforeFilter: beforeFilter,
            //   afterFilter: afterFilter,
            //   removedCount: beforeFilter - afterFilter,
            //   expectedRemoved: 1
            // });
            
            // Safety check: if more than 1 fence was removed, log warning
            if (beforeFilter - afterFilter > 1) {
              console.warn('WARNING: More than 1 fence was removed!', {
                removedCount: beforeFilter - afterFilter,
                fenceName: fenceName,
                allUnsavedFences: this.fences.filter(f => !f.id).map(f => f.name)
              });
            }
          }
          
          // console.log('onPolygonDeleted (edit mode) - fence removed from array', {
          //   fencesCountAfter: this.fences.length,
          //   remainingFences: this.fences.map(f => ({ id: f.id, name: f.name }))
          // });
          
          // Auto-save: immediately emit update to parent component
          const fencesToEmit = JSON.parse(JSON.stringify(this.fences));
          // console.log('onPolygonDeleted (edit mode) emitting fences-updated', {
          //   fencesCount: fencesToEmit.length,
          //   isClearing: this.isClearing
          // });
          if (!this.isClearing) {
            this.$emit('fences-updated', fencesToEmit);
          }
        }
      });
    },
    
    extractCoordinates(layer) {
      const latlngs = layer.getLatLngs()[0];
      const coordinates = [];
      
      latlngs.forEach((latlng) => {
        coordinates.push([latlng.lng, latlng.lat]); // [longitude, latitude]
      });
      
      // Pastikan polygon tertutup
      if (coordinates.length > 0) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coordinates.push([first[0], first[1]]);
        }
      }
      
      return coordinates;
    },
    async loadGeoFences() {
      if (this.isCreateMode || !this.campaignId) {
        return;
      }
      
      try {
        this.loading = true;
        const response = await this.$store.dispatch('SimplifiGetGeoFences', this.campaignId);
        
        if (response.result === 'success') {
          let fences = response.data.geo_fences || [];
          
          // Filter fences based on mode
          if (this.isConversionMode) {
            // Only show Conversion fences
            fences = fences.filter(fence => 
              fence.geo_fence_type_name === 'Conversion'
            );
          } else {
            // Only show Target fences (exclude Conversion fences)
            fences = fences.filter(fence => 
              fence.geo_fence_type_name === 'Target' || 
              !fence.geo_fence_type_name || 
              fence.geo_fence_type_name === null
            );
          }
          
          this.fences = fences;
          this.renderFencesOnMap();
          
          // Extract recency_id from first fence if exists, otherwise use default (1 month)
          if (this.fences.length > 0 && this.fences[0].recency_id) {
            this.recencyId = this.fences[0].recency_id;
          } else {
            // No recency_id found, use default: 1 month (ID: 4)
            this.recencyId = 4;
          }
          this.$emit('recency-updated', this.recencyId);
          
          // Auto-save: immediately emit update to parent component
          this.$emit('fences-updated', JSON.parse(JSON.stringify(this.fences)));
        }
      } catch (error) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              ${error.message || 'Failed to load geo fences'}
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
      } finally {
        this.loading = false;
      }
    },
    
    renderFencesOnMap() {
      const L = window.L;
      
      // Clear existing drawn items
      this.drawnItems.clearLayers();
      
      this.fences.forEach((fence) => {
        // Convert coordinates ke LatLng array
        const latlngs = fence.bid_area.coordinates[0].map(coord => 
          L.latLng(coord[1], coord[0]) // [longitude, latitude] -> LatLng(lat, lng)
        );
        
        // Buat polygon layer
        const polygon = L.polygon(latlngs, {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2
        });
        
        // Store fence data
        polygon._geoFenceData = {
          id: fence.id,
          name: fence.name,
          type: fence.geo_fence_type_name || 'Target',
          coordinates: fence.bid_area.coordinates[0]
        };
        
        // Tambahkan ke map
        this.drawnItems.addLayer(polygon);
      });
      
      // Fit map ke semua polygons
      if (this.drawnItems.getLayers().length > 0) {
        this.map.fitBounds(this.drawnItems.getBounds());
      }
    },
    
    loadExistingFences(fences) {
      // Method to load existing fences from parent component (edit mode)
      this.fences = fences || [];
      this.renderFencesOnMap();
      
      // Extract recency_id from first fence if exists, otherwise use default (1 month)
      if (fences && fences.length > 0 && fences[0].recency_id) {
        this.recencyId = fences[0].recency_id;
      } else {
        // No recency_id found, use default: 1 month (ID: 4)
        this.recencyId = 4;
      }
      this.$emit('recency-updated', this.recencyId);
      
      // Auto-save: langsung emit update ke parent component
      this.$emit('fences-updated', JSON.parse(JSON.stringify(this.fences)));
    },
    
    loadDraftFences(fences) {
      // Method to load draft fences from parent component (create mode)
      // This is called when user navigates back to step 1 after creating fences
      // Skip if clearAll is in progress
      if (this.isClearing) {
        // console.log('loadDraftFences skipped - clearAll in progress');
        return;
      }
      
      // CRITICAL: If fences is empty array, don't load anything
      // This prevents restoring fences after clearAll()
      if (!fences || !Array.isArray(fences) || fences.length === 0) {
        // console.log('loadDraftFences skipped - empty or invalid fences array');
        return;
      }
      
      if (!this.isCreateMode) {
        // console.log('loadDraftFences skipped - not in create mode');
        return;
      }
      
      // console.log('loadDraftFences called', {
      //   fencesCount: fences.length,
      //   isClearing: this.isClearing
      // });
      
      const L = window.L;
      if (!L) {
        console.warn('Leaflet not available');
        return;
      }
      
      // If map is not initialized yet, wait for it
      if (!this.map) {
        // Wait for map to be initialized
        const checkMap = setInterval(() => {
          if (this.map && !this.isClearing) {
            clearInterval(checkMap);
            this.loadDraftFences(fences);
          }
        }, 100);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkMap);
        }, 5000);
        return;
      }
      
      // Clear existing drawn items
      this.drawnItems.clearLayers();
      this.draftFences = [];
      
      // Determine default fence type based on conversion mode
      const defaultFenceType = this.isConversionMode ? 'Conversion' : 'Target';
      
      // Load each fence
      fences.forEach((fence, index) => {
        if (!fence.bid_area || !fence.bid_area.coordinates || !fence.bid_area.coordinates[0]) {
          return;
        }
        
        // Convert coordinates to LatLng array
        const latlngs = fence.bid_area.coordinates[0].map(coord => 
          L.latLng(coord[1], coord[0]) // [longitude, latitude] -> LatLng(lat, lng)
        );
        
        // Create polygon layer
        const polygon = L.polygon(latlngs, {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2,
          weight: 2
        });
        
        // Store fence data in layer
        polygon._geoFenceData = {
          coordinates: fence.bid_area.coordinates[0],
          name: fence.name || `Fence ${index + 1}`,
          type: fence.geo_fence_type_name || defaultFenceType,
          draftIndex: index
        };
        
        // Add to drawnItems and map
        this.drawnItems.addLayer(polygon);
        
        // Add to draftFences array
        this.draftFences.push({
          name: fence.name || `Fence ${index + 1}`,
          geo_fence_type_name: fence.geo_fence_type_name || defaultFenceType,
          bid_area: {
            type: 'Polygon',
            coordinates: [fence.bid_area.coordinates[0]]
          }
        });
      });
      
      // Fit map to show all polygons
      if (this.drawnItems.getLayers().length > 0) {
        this.map.fitBounds(this.drawnItems.getBounds());
      }
      
      // Extract recency_id from first fence if exists, otherwise use default (1 month)
      if (fences.length > 0 && fences[0].recency_id) {
        this.recencyId = fences[0].recency_id;
      } else {
        // No recency_id found, use default: 1 month (ID: 4)
        this.recencyId = 4;
      }
      
      // Don't emit update to parent since parent already has this data
      // This prevents infinite loop
    },
    
    deleteFence(fenceId, fenceIndex, fenceName) {
      // Don't delete directly to Simplifi - only remove from local state
      // Delete from Simplifi will be done when campaign is saved
      
      const L = window.L;
      if (!L || !this.map) {
        return;
      }
      
      // Prevent deleting the last fence for non-draft campaigns
      // Non-draft campaigns must have at least one geo fence (geo-targeting mode is locked)
      if (!this.isCreateMode && !this.isDraftCampaign) {
        const currentFencesCount = this.fences.length;
        if (currentFencesCount <= 1) {
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Cannot Delete</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                This campaign has already run and uses Geo Fences. At least one geo fence must remain. 
                The Geo Targeting mode cannot be changed for campaigns that have already run.
              </p>
            `,
            duration: 5000,
            dangerouslyUseHTMLString: true
          });
          return;
        }
      }
      
      // console.log('deleteFence called', {
      //   fenceId: fenceId,
      //   fenceIndex: fenceIndex,
      //   fenceName: fenceName,
      //   fenceIdType: typeof fenceId,
      //   fencesCountBefore: this.fences.length,
      //   fences: this.fences.map((f, idx) => ({ index: idx, id: f.id, idType: typeof f.id, name: f.name }))
      // });
      
      // Find fence in array - handle both saved fences (with ID) and new fences (without ID)
      let fenceToDelete = null;
      let actualFenceId = fenceId;
      
      if (fenceId !== undefined && fenceId !== null) {
        // Fence has ID - find by ID
        fenceToDelete = this.fences.find(f => f.id === fenceId);
        if (!fenceToDelete) {
          console.warn('deleteFence - fence with ID not found in array', {
            fenceId: fenceId,
            availableFenceIds: this.fences.map(f => f.id)
          });
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Fence not found
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
          return;
        }
      } else {
        // Fence doesn't have ID (new fence) - find by index or name
        if (fenceIndex !== undefined && fenceIndex !== null && this.fences[fenceIndex]) {
          fenceToDelete = this.fences[fenceIndex];
          // console.log('deleteFence - found fence by index', {
          //   fenceIndex: fenceIndex,
          //   fenceName: fenceToDelete.name,
          //   fenceId: fenceToDelete.id
          // });
        } else if (fenceName) {
          // Fallback: find by name
          fenceToDelete = this.fences.find(f => !f.id && f.name === fenceName);
          if (fenceToDelete) {
            // console.log('deleteFence - found fence by name', {
            //   fenceName: fenceName,
            //   fenceId: fenceToDelete.id
            // });
          }
        }
        
        if (!fenceToDelete) {
          console.warn('deleteFence - new fence not found in array', {
            fenceIndex: fenceIndex,
            fenceName: fenceName,
            availableFences: this.fences.map((f, idx) => ({ index: idx, id: f.id, name: f.name }))
          });
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Fence not found
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
          return;
        }
        
        // For new fences, use a unique identifier (name + index) to find the layer
        actualFenceId = fenceToDelete.name || `fence_${fenceIndex}`;
      }
      
      // Set flag to prevent onPolygonDeleted from processing
      this.isDeletingFence = true;
      
      // CRITICAL: Remove event listener FIRST to prevent onPolygonDeleted from being triggered
      // This prevents double deletion and incorrect deletion of unsaved fences
      this.map.off(L.Draw.Event.DELETED, this.onPolygonDeleted);
      
      // Remove polygon from map based on fence ID or name
      let foundLayer = null;
      this.drawnItems.eachLayer((layer) => {
        if (!layer._geoFenceData) return;
        
        // Match by ID if fence has ID
        if (fenceId !== undefined && fenceId !== null && layer._geoFenceData.id === fenceId) {
          foundLayer = layer;
        }
        // Match by name if fence doesn't have ID (new fence)
        else if ((fenceId === undefined || fenceId === null) && 
                 fenceToDelete && 
                 layer._geoFenceData.name === fenceToDelete.name) {
          foundLayer = layer;
        }
      });
      
      // Remove layer from map (this won't trigger DELETED event because listener is off)
      if (foundLayer) {
        // console.log('deleteFence - removing layer from map', {
        //   layerId: foundLayer._geoFenceData.id,
        //   layerName: foundLayer._geoFenceData.name
        // });
        // Remove from drawnItems first, then from map
        if (this.drawnItems.hasLayer(foundLayer)) {
          this.drawnItems.removeLayer(foundLayer);
        }
        if (this.map.hasLayer(foundLayer)) {
          this.map.removeLayer(foundLayer);
        }
      } else {
        console.warn('deleteFence - layer not found on map', {
          fenceId: fenceId,
          layersOnMap: this.drawnItems.getLayers().map(l => ({
            id: l._geoFenceData.id,
            name: l._geoFenceData.name
          }))
        });
      }
      
      // Re-add event listener after layer removal
      this.map.on(L.Draw.Event.DELETED, this.onPolygonDeleted);
      
      // Remove from fences array (will be synced when campaign is saved)
      // CRITICAL: Use the found fence object to ensure only the exact fence is removed
      const fencesBefore = this.fences.length;
      
      if (fenceId !== undefined && fenceId !== null) {
        // Remove by ID (saved fence)
        this.fences = this.fences.filter(fence => fence.id !== fenceId);
      } else {
        // Remove by index or name (new fence)
        if (fenceIndex !== undefined && fenceIndex !== null && this.fences[fenceIndex] === fenceToDelete) {
          // Remove by index (most reliable for new fences)
          this.fences.splice(fenceIndex, 1);
        } else if (fenceToDelete) {
          // Fallback: remove by finding the exact object
          const indexToRemove = this.fences.indexOf(fenceToDelete);
          if (indexToRemove !== -1) {
            this.fences.splice(indexToRemove, 1);
          } else {
            // Last resort: remove by name (only if no ID)
            this.fences = this.fences.filter(fence => {
              if (fence.id) return true; // Keep all saved fences
              return fence.name !== fenceToDelete.name; // Remove only if name matches
            });
          }
        }
      }
      
      const fencesAfter = this.fences.length;
      
      // console.log('deleteFence - fence removed from array', {
      //   fencesCountBefore: fencesBefore,
      //   fencesCountAfter: fencesAfter,
      //   deletedFenceId: fenceId,
      //   deletedFenceName: fenceToDelete.name,
      //   remainingFences: this.fences.map(f => ({ id: f.id, name: f.name }))
      // });
      
      // Safety check: if more than 1 fence was removed, something is wrong
      if (fencesBefore - fencesAfter > 1) {
        console.error('ERROR: More than 1 fence was removed!', {
          removedCount: fencesBefore - fencesAfter,
          deletedFenceId: fenceId,
          allFencesBefore: this.fences.map(f => ({ id: f.id, name: f.name }))
        });
      }
      
      // Reset flag after array update
      this.isDeletingFence = false;
      
      // Auto-save: immediately emit update to parent component
      const fencesToEmit = JSON.parse(JSON.stringify(this.fences));
      // console.log('deleteFence emitting fences-updated', {
      //   fencesCount: fencesToEmit.length,
      //   deletedFenceId: fenceId
      // });
      if (!this.isClearing) {
        this.$emit('fences-updated', fencesToEmit);
      }
      
      Notification({
        message: `
          <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
            <i class="el-notification__icon el-icon-success" style="margin-right: 10px;"></i> 
            <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Success</span>
          </p>
          <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
            Geo fence will be deleted when campaign is saved
          </p>
        `,
        duration: 3000,
        dangerouslyUseHTMLString: true
      });
    },
    
    
    
    clearAll() {
      // console.log('=== clearAll() CALLED ===', {
      //   isCreateMode: this.isCreateMode,
      //   draftFencesCount: this.draftFences.length,
      //   fencesCount: this.fences.length,
      //   drawnItemsLayersCount: this.drawnItems ? this.drawnItems.getLayers().length : 0,
      //   draftFences: this.draftFences.map(f => f.name),
      //   fences: this.fences.map(f => f.name || f.id)
      // });
      
      // Set flag to prevent event handlers from processing during clearAll
      this.isClearing = true;
      
      // First, clear arrays to prevent event handlers from trying to update them
      this.fences = [];
      this.draftFences = [];
      this.editingNameIndex = null;
      this.editingNameId = null;
      
      // Then remove all layers from map
      if (this.map && this.drawnItems) {
        const L = window.L;
        if (!L) {
          console.warn('Leaflet not available during clearAll');
          this.isClearing = false;
          return;
        }
        
        // CRITICAL: Remove event listener FIRST before any layer removal
        // This prevents onPolygonDeleted from being triggered
        this.map.off(L.Draw.Event.DELETED, this.onPolygonDeleted);
        
        // Get all layers BEFORE clearing (snapshot)
        const layersToRemove = [];
        this.drawnItems.eachLayer((layer) => {
          layersToRemove.push(layer);
        });
        
        // console.log('Layers to remove:', layersToRemove.length);
        
        // IMPORTANT: Remove layers from map FIRST, then from drawnItems
        // This prevents Leaflet from triggering DELETED events
        layersToRemove.forEach((layer, index) => {
          try {
            // console.log(`Removing layer ${index + 1}/${layersToRemove.length}`);
            
            // Remove from map FIRST (this prevents event from being triggered)
            if (this.map.hasLayer(layer)) {
              this.map.removeLayer(layer);
            }
            // Then remove from drawnItems (this is safe now because layer is already removed from map)
            if (this.drawnItems.hasLayer(layer)) {
              this.drawnItems.removeLayer(layer);
            }
          } catch (error) {
            console.error('Error removing layer during clearAll:', error, layer);
          }
        });
        
        // Now it's safe to call clearLayers() - it should be empty, but just in case
        // This should not trigger any events because all layers are already removed from map
        const remainingBeforeClear = this.drawnItems.getLayers().length;
        // console.log('Remaining layers before clearLayers():', remainingBeforeClear);
        
        if (remainingBeforeClear > 0) {
          // If there are still layers, remove them individually
          const stillRemaining = [];
          this.drawnItems.eachLayer((layer) => {
            stillRemaining.push(layer);
          });
          stillRemaining.forEach((layer) => {
            try {
              if (this.map.hasLayer(layer)) {
                this.map.removeLayer(layer);
              }
              if (this.drawnItems.hasLayer(layer)) {
                this.drawnItems.removeLayer(layer);
              }
            } catch (error) {
              console.error('Error removing remaining layer:', error);
            }
          });
        }
        
        // Final clear (should be empty now)
        this.drawnItems.clearLayers();
        
        // Re-add event listener after clearing is complete
        this.map.on(L.Draw.Event.DELETED, this.onPolygonDeleted);
        
        // Final check: remove any remaining polygon/rectangle layers on map
        // (in case some were added directly to map instead of drawnItems)
        const remainingLayers = [];
        this.map.eachLayer((layer) => {
          // Skip tile layers, controls, and drawnItems feature group
          if (layer instanceof L.TileLayer || 
              layer instanceof L.Control || 
              layer === this.drawnItems) {
            return;
          }
          // Collect any polygon/rectangle layers that might be left
          if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
            remainingLayers.push(layer);
          }
        });
        
        // console.log('Remaining polygon/rectangle layers found:', remainingLayers.length);
        
        // Remove any remaining layers
        remainingLayers.forEach((layer) => {
          try {
            this.map.removeLayer(layer);
          } catch (error) {
            console.error('Error removing remaining layer:', error);
          }
        });
        
        // Final verification
        const finalLayersCount = this.drawnItems.getLayers().length;
        // console.log('Final layers count in drawnItems:', finalLayersCount);
        
        if (finalLayersCount > 0) {
          console.warn('WARNING: Some layers still remain after clearAll!', {
            count: finalLayersCount
          });
        }
      }
      
      // Reset flag AFTER all clearing operations are complete
      // Use setTimeout to ensure all async operations are done
      setTimeout(() => {
        // Double-check: ensure arrays are still empty before emitting
        // This prevents race conditions where other handlers might have modified arrays
        if (this.isCreateMode) {
          this.draftFences = [];
        } else {
          this.fences = [];
        }
        
        // Emit update for both modes - ALWAYS emit empty array
        const emptyArray = [];
        if (this.isCreateMode) {
          this.$emit('fences-updated', emptyArray);
        } else {
          this.$emit('fences-updated', emptyArray);
        }
        
        // Keep default recency (1 month) even when clearing
        this.recencyId = 4;
        this.$emit('recency-updated', 4);
        
        // Reset flag AFTER emitting (to prevent any handlers from interfering)
        this.isClearing = false;
        
        // console.log('clearAll() completed', {
        //   draftFencesCount: this.draftFences.length,
        //   fencesCount: this.fences.length,
        //   isClearing: this.isClearing
        // });
      }, 100); // Small delay to ensure all operations are complete
    },
    // Method untuk mendapatkan draftFences (untuk validasi di parent)
    getDraftFences() {
      if (this.isCreateMode) {
        return this.draftFences;
      } else {
        // Di edit mode, return fences
        return this.fences;
      }
    },
    onRecencyChange(value) {
      // Ensure recency is never null - default to 4 (1 month) if null
      const recencyValue = value || 4;
      this.recencyId = recencyValue;
      this.$emit('recency-updated', recencyValue);
    },
    removeDraftFence(index) {
      // Validate index
      if (index < 0 || index >= this.draftFences.length) {
        return;
      }
      
      // Find layer by draftIndex (more reliable than matching coordinates)
      let foundLayer = null;
      this.drawnItems.eachLayer((layer) => {
        if (layer._geoFenceData && layer._geoFenceData.draftIndex === index) {
          foundLayer = layer;
        }
      });
      
      // Remove layer from map
      if (foundLayer) {
        this.map.removeLayer(foundLayer);
        this.drawnItems.removeLayer(foundLayer);
      }
      
      // Remove from draftFences array
      this.draftFences.splice(index, 1);
      
      // Update draftIndex for remaining layers (because array index changed)
      this.drawnItems.eachLayer((layer) => {
        if (layer._geoFenceData && layer._geoFenceData.draftIndex !== null) {
          if (layer._geoFenceData.draftIndex > index) {
            // Decrement index for layers after the deleted one
            layer._geoFenceData.draftIndex--;
          }
        }
      });
      
      // Auto-save: immediately emit update to parent
      this.$emit('fences-updated', JSON.parse(JSON.stringify(this.draftFences)));
    },
    startEditName(index, id, currentName) {
      // index untuk create mode, id untuk edit mode
      if (this.isCreateMode) {
        this.editingNameIndex = index;
        this.editingNameId = null;
      } else {
        this.editingNameIndex = null;
        this.editingNameId = id;
      }
      this.editingNameValue = currentName || '';
      
      // Focus input setelah DOM update
      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.length > 0) {
          this.$refs.nameInput[0].focus();
          this.$refs.nameInput[0].select();
        } else if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
          this.$refs.nameInput.select();
        }
      });
    },
    saveFenceName(index, id) {
      const newName = (this.editingNameValue || '').trim();

      if (newName.length > 255) {
          Notification.error({
            title: 'Error',
            message: 'Geo Fance Name Maximal 255 Name Character (saat ini: ' + newName.length + ' Name Character).',
            customClass : 'error-notification-simplifi'
          });
          this.cancelEditName();
          return;   
      }
      
      if (!newName) {
        // If name is empty, cancel edit
        this.cancelEditName();
        return;
      }
      
      if (this.isCreateMode && index !== null) {
        // Create mode: update draftFences
        if (this.draftFences[index]) {
          this.draftFences[index].name = newName;
          
          // Update layer data as well
          this.drawnItems.eachLayer((layer) => {
            if (layer._geoFenceData && layer._geoFenceData.draftIndex === index) {
              layer._geoFenceData.name = newName;
            }
          });
          
          // Auto-save: immediately emit update
          this.$emit('fences-updated', JSON.parse(JSON.stringify(this.draftFences)));
        }
      } else if (!this.isCreateMode && id !== null) {
        // Edit mode: update fences
        const fenceIndex = this.fences.findIndex(f => f.id === id);
        if (fenceIndex !== -1) {
          this.fences[fenceIndex].name = newName;
          
          // Update layer data as well
          this.drawnItems.eachLayer((layer) => {
            if (layer._geoFenceData && layer._geoFenceData.id === id) {
              layer._geoFenceData.name = newName;
            }
          });
          
          // Auto-save: immediately emit update to parent
          this.$emit('fences-updated', JSON.parse(JSON.stringify(this.fences)));
        }
      }
      
      // Reset editing state
      this.cancelEditName();
    },
    cancelEditName() {
      this.editingNameIndex = null;
      this.editingNameId = null;
      this.editingNameValue = '';
    },
    coordinatesMatch(coord1, coord2) {
      // Compare two coordinate arrays
      if (!coord1 || !coord2 || coord1.length !== coord2.length) {
        return false;
      }
      
      // Compare each coordinate point (with small tolerance for floating point)
      for (let i = 0; i < coord1.length; i++) {
        const [lng1, lat1] = coord1[i];
        const [lng2, lat2] = coord2[i];
        if (Math.abs(lng1 - lng2) > 0.0001 || Math.abs(lat1 - lat2) > 0.0001) {
          return false;
        }
      }
      return true;
    },
    
    getPointCount(fence) {
      if (fence.bid_area && fence.bid_area.coordinates && fence.bid_area.coordinates[0]) {
        return fence.bid_area.coordinates[0].length;
      }
      return 0;
    },
    getDraftFencePointCount(fence) {
      if (fence.bid_area && fence.bid_area.coordinates && fence.bid_area.coordinates[0]) {
        return fence.bid_area.coordinates[0].length;
      }
      return 0;
    },
    setActiveTool(tool) {
      this.activeTool = tool;
      // Implement tool switching logic here
      if (tool === 'pan') {
        // Enable pan mode
        if (this.map) {
          this.map.dragging.enable();
        }
      } else if (tool === 'rectangle') {
        // Enable rectangle drawing
        // This would need to be implemented with Leaflet Draw
      } else if (tool === 'polygon') {
        // Enable polygon drawing (already implemented)
      }
    },
    startDrawing() {
      this.setActiveTool('polygon');
    },
    downloadGeoData() {
      // Implement download functionality
      Notification({
        message: `
          <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
            <i class="el-notification__icon el-icon-info" style="margin-right: 10px;"></i> 
            <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Info</span>
          </p>
          <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
            Download functionality will be implemented
          </p>
        `,
        duration: 3000,
        dangerouslyUseHTMLString: true
      });
    },
    handleSelectionChange(selection) {
      this.selectedFences = selection;
    },
    onSearchInput() {
      // Debounce search input - wait 500ms after user stops typing
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Only auto-search if query is long enough (min 3 chars for geocoding)
      const query = (this.searchQuery || '').trim();
      if (query.length >= 3) {
        this.searchDebounceTimer = setTimeout(() => {
          // Don't auto-trigger on input, only on Enter or button click
          // This prevents too many API calls while typing
        }, 500);
      }
    },
    async handleSearch() {
      // Prevent multiple simultaneous requests
      if (this.searching) {
        return;
      }
      
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return;
      }
      
      const query = this.searchQuery.trim();
      
      // Ensure Mapbox access token is available
      if (!this.mapboxAccessToken) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Mapbox access token is not configured. Please set VUE_APP_MAPBOX_ACCESS_TOKEN in .env.
            </p>
          `,
          duration: 4000,
          dangerouslyUseHTMLString: true
        });
        return;
      }
      
      // Rate limiting: max 1 request per second (client-side to reduce cost and avoid abuse)
      const now = Date.now();
      const timeSinceLastSearch = now - this.lastSearchTime;
      if (timeSinceLastSearch < 1000) {
        // Silently ignore request if too fast (rate limiting)
        return;
      }
      
      // Check cache first (cache doesn't need rate limiting)
      if (this.searchCache[query]) {
        const cachedResult = this.searchCache[query];
        this.map.setView([cachedResult.lat, cachedResult.lng], 13);
        
        // Add marker
        const L = window.L;
        if (this.searchMarker) {
          this.map.removeLayer(this.searchMarker);
        }
        
        this.searchMarker = L.marker([cachedResult.lat, cachedResult.lng], {
          icon: L.divIcon({
            className: 'search-marker',
            html: '<div style="background: #409eff; width: 20px; height: 20px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        }).addTo(this.map);
        
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-success" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Success</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Found: ${cachedResult.display_name}
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
        // Cache hit - no API call needed, but rate limit still applies
        return;
      }
      
      // Set searching flag for API requests
      this.searching = true;
      
      // Update lastSearchTime when starting API request (not for cache hits)
      this.lastSearchTime = Date.now();
      
      try {
        // Check if it's lat/long format (e.g., "40.7128, -74.0060" or "40.7128,-74.0060")
        const latLngMatch = query.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
        
        if (latLngMatch) {
          // Direct lat/lng coordinates
          const lat = parseFloat(latLngMatch[1]);
          const lng = parseFloat(latLngMatch[2]);
          
          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  Invalid coordinates. Please use format: latitude, longitude
                </p>
              `,
              duration: 3000,
              dangerouslyUseHTMLString: true
            });
            this.searching = false;
            return;
          }
          
          this.map.setView([lat, lng], 13);
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-success" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Success</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Map centered at ${lat}, ${lng}
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
        } else {
          // Validasi minimum karakter untuk geocoding (minimal 3 karakter)
          if (query.length < 3) {
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Warning</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  Please enter at least 3 characters to search
                </p>
              `,
              duration: 3000,
              dangerouslyUseHTMLString: true
            });
            // Note: searching will be set to false in finally block
            return;
          }
          
          // Geocoding using Mapbox Geocoding API (forward geocoding)
          // Docs: https://docs.mapbox.com/api/search/geocoding/
          const mapboxUrl = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(query)}&limit=1&access_token=${this.mapboxAccessToken}`;
          
          const response = await fetch(mapboxUrl);
          
          if (!response.ok) {
            // Handle specific HTTP status codes from Mapbox
            let errorMsg = 'Failed to search location. Please try again.';
            
            if (response.status === 401) {
              errorMsg = 'Mapbox: Not authorized. Please check your access token.';
            } else if (response.status === 403) {
              errorMsg = 'Mapbox: Forbidden. Please check your account and token restrictions.';
            } else if (response.status === 404) {
              errorMsg = 'Mapbox: Invalid endpoint or missing query.';
            } else if (response.status === 422) {
              errorMsg = 'Mapbox: Invalid search parameters. Please refine your query.';
            } else if (response.status === 429) {
              errorMsg = 'Mapbox: Rate limit exceeded. Please wait a moment before searching again.';
            }
            
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  ${errorMsg}
                </p>
              `,
              duration: 4000,
              dangerouslyUseHTMLString: true
            });
            return;
          }
          
          const data = await response.json();
          const features = (data && data.features) ? data.features : [];
          
          if (features.length > 0) {
            const feature = features[0];
            const coords = feature && feature.geometry && feature.geometry.coordinates
              ? feature.geometry.coordinates
              : null;
            
            if (!coords || coords.length < 2) {
              Notification({
                message: `
                  <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                    <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                    <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
                  </p>
                  <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                    Mapbox response did not contain valid coordinates.
                  </p>
                `,
                duration: 4000,
                dangerouslyUseHTMLString: true
              });
              return;
            }
            
            // Mapbox coordinates format: [lon, lat]
            const lng = parseFloat(coords[0]);
            const lat = parseFloat(coords[1]);
            
            // Safety validation for lat/lng ranges
            if (
              isNaN(lat) || isNaN(lng) ||
              lat < -90 || lat > 90 ||
              lng < -180 || lng > 180
            ) {
              Notification({
                message: `
                  <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                    <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                    <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
                  </p>
                  <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                    Invalid coordinates from Mapbox. Please try a different search term.
                  </p>
                `,
                duration: 4000,
                dangerouslyUseHTMLString: true
              });
              return;
            }
            
            const displayName =
              (feature.properties && feature.properties.name) ||
              feature.place_name ||
              query;
            
            // Cache the result (limit cache size to prevent memory issues)
            if (Object.keys(this.searchCache).length > 50) {
              // Remove oldest entry (simple FIFO)
              const firstKey = Object.keys(this.searchCache)[0];
              delete this.searchCache[firstKey];
            }
            this.searchCache[query] = {
              lat: lat,
              lng: lng,
              display_name: displayName
            };
            
            this.map.setView([lat, lng], 13);
            
            // Add marker to show the location
            const L = window.L;
            if (this.searchMarker) {
              this.map.removeLayer(this.searchMarker);
            }
            
            this.searchMarker = L.marker([lat, lng], {
              icon: L.divIcon({
                className: 'search-marker',
                html: '<div style="background: #409eff; width: 20px; height: 20px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
              })
            }).addTo(this.map);
            
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-success" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Success</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  Found: ${displayName}
                </p>
              `,
              duration: 3000,
              dangerouslyUseHTMLString: true
            });
          } else {
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Warning</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  Location not found. Please try a different search term.
                </p>
              `,
              duration: 3000,
              dangerouslyUseHTMLString: true
            });
          }
        }
      } catch (error) {
        console.error('Search error:', error);
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Failed to search location. Please try again.
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
      } finally {
        this.searching = false;
        // Note: lastSearchTime already updated at the start of request
        // to prevent rapid-fire requests
      }
    },
    clearSearch() {
      this.searchQuery = '';
      if (this.searchMarker) {
        this.map.removeLayer(this.searchMarker);
        this.searchMarker = null;
      }
      // Clear debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },
    async loadRecencyOptions() {
      try {
        this.loadingRecency = true;
        const response = await this.$store.dispatch('SimplifiGetGeoFenceRecencyOptions');
        
        if (response.result === 'success' && response.data) {
          // Format response dari Simplifi API: { recencies: [{ id, name, resource }, ...] }
          if (response.data.recencies && Array.isArray(response.data.recencies)) {
            // Map recencies dari API ke format select (id, label)
            this.recencyOptions = response.data.recencies.map(item => ({
              id: item.id,
              label: item.name
            }));
          } else if (Array.isArray(response.data)) {
            // Fallback jika format langsung array
            this.recencyOptions = response.data.map(item => ({
              id: item.id || item.recency_id,
              label: item.name || item.label || item.recency_name || `Recency ${item.id || item.recency_id}`
            }));
          }
          
          // Set default recency to "1 month" (ID: 4) if not already set
          if (!this.recencyId) {
            this.recencyId = 4;
            this.$emit('recency-updated', 4);
          }
        }
      } catch (error) {
        console.error('Failed to load recency options:', error);
        // Fallback ke default options jika API gagal (data dari Simplifi API)
        this.recencyOptions = [
          { id: 4, label: '1 month' },
          { id: 1, label: '2 weeks' },
          { id: 3, label: '1 week' },
          { id: 2, label: '1 day' },
          { id: 10, label: '1 hour' },
          { id: 9, label: '30 minutes' },
          { id: 8, label: '15 minutes' },
          { id: 7, label: '10 minutes' },
          { id: 6, label: '5 minutes' },
          { id: 5, label: 'Instant' }
        ];
        // Set default recency to "1 month" (ID: 4) if API fails
        this.recencyId = 4;
        this.$emit('recency-updated', 4);
      } finally {
        this.loadingRecency = false;
      }
    },
  },
  computed: {
    filteredDraftFences() {
      if (!this.filterQuery) {
        return this.draftFences;
      }
      const query = this.filterQuery.toLowerCase();
      return this.draftFences.filter(fence => 
        (fence.name || '').toLowerCase().includes(query)
      );
    },
    filteredFences() {
      if (!this.filterQuery) {
        return this.fences;
      }
      const query = this.filterQuery.toLowerCase();
      return this.fences.filter(fence => 
        (fence.name || '').toLowerCase().includes(query)
      );
    },
    isSearchRateLimited() {
      // Check if we're in rate limit cooldown period (1 second minimum)
      const now = Date.now();
      const timeSinceLastSearch = now - this.lastSearchTime;
      return timeSinceLastSearch < 1000;
    }
  }
};
</script>

<style scoped>
.geo-fence-map-container {
  width: 100%;
  background: #fff;
}

/* Search Bar (Simple Clean Style) */
.geo-fence-search-bar {
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
}

.search-input {
  width: 100%;
}

/* Map Container */
.geo-fence-map {
  height: 500px;
  width: 100%;
  position: relative;
}

/* Table Section (Simple Clean Style) */
.geo-fences-table-section {
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
}

.filter-section {
  margin-bottom: 12px;
}

.filter-input {
  width: 100%;
  max-width: 300px;
}

.table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar {
  width: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.fences-table {
  width: 100%;
}

.recency-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  gap: 12px;
}

.recency-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

.recency-select {
  min-width: 200px;
}

.fence-name-text {
  font-size: 14px;
  color: #303133;
}

.fence-name-display {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.fence-name-edit-icon {
  opacity: 0;
  transition: opacity 0.2s;
  color: #409eff;
  font-size: 14px;
  cursor: pointer;
}

.fence-name-display:hover .fence-name-edit-icon {
  opacity: 1;
}

.fence-name-edit {
  width: 100%;
}

.fence-name-input-inline {
  width: 100%;
}

</style>

<!-- Global Leaflet styles -->
<style>
/* Better polygon styling */
.leaflet-interactive {
  stroke-width: 3 !important;
  stroke-opacity: 0.8 !important;
  fill-opacity: 0.3 !important;
}

/* Drawing tooltip styling */
.leaflet-draw-tooltip {
  background: rgba(0, 0, 0, 0.8) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 4px !important;
  padding: 5px 10px !important;
  font-size: 12px !important;
}

/* Improve Leaflet attribution */
.leaflet-control-attribution {
  background: rgba(255, 255, 255, 0.8) !important;
  padding: 2px 5px !important;
  font-size: 11px !important;
  border-radius: 4px !important;
}

/* Search marker styling */
.search-marker {
  background: transparent !important;
  border: none !important;
}
</style>

